import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Users, ShieldCheck, KeyRound } from "lucide-react";
import { Button } from "./ui/button";
import { API_BASE } from "../DataContext";

const GROUP_META = {
  content: { label: "Content", desc: "Blog posts, resources" },
  procurement: { label: "Procurement", desc: "Vendors, Items, POs, Invoices, GRN, Documents" },
  customer_portal: { label: "Customer Portal", desc: "Customers, Sales Documents, Ledger" },
};

const EMPTY = { id: "", name: "", email: "", password: "", role: "admin", groups: [], is_active: true };

export function AdminUsers({ token, onUnauthorized }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.status === 401) return onUnauthorized?.();
    if (r.status === 403) return setRows({ forbidden: true });
    setRows(await r.json());
  }, [token, onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const method = editing.id ? "PUT" : "POST";
      const url = editing.id ? `${API_BASE}/api/admin/users/${editing.id}` : `${API_BASE}/api/admin/users`;
      const body = { ...editing };
      if (!body.password) delete body.password;
      delete body.id;
      const r = await fetch(url, {
        method, headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Save failed");
      setEditing(null); load();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!confirm(`Delete ${u.name}?`)) return;
    const r = await fetch(`${API_BASE}/api/admin/users/${u.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return alert((await r.json().catch(() => ({}))).detail || "Delete failed");
    load();
  };

  if (rows?.forbidden) return <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">You need Super Admin access to view this module.</div>;

  const toggleGroup = (g) => {
    const has = editing.groups.includes(g);
    setEditing({ ...editing, groups: has ? editing.groups.filter((x) => x !== g) : [...editing.groups, g] });
  };

  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-[#0a2a5e] flex items-center gap-2"><Users className="h-5 w-5" /> User Management</h2>
          <p className="text-xs text-slate-500">Create and manage admin users. Assign module groups to control what each user can see.</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY })} data-testid="add-user-btn" className="rounded-full bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white">
          <Plus className="h-4 w-4 mr-1" /> Add User
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Role</th>
              <th className="px-4 py-3 font-semibold">Groups</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Last Login</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!rows && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {rows && !rows.forbidden && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No users yet.</td></tr>}
            {rows?.map?.((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-sky-50/50">
                <td className="px-4 py-3 font-semibold text-[#0a2a5e]">{u.name}</td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${u.role === "super_admin" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-700"}`}>
                    {u.role === "super_admin" ? <><ShieldCheck className="inline h-3 w-3 mr-0.5" />Super Admin</> : "Admin"}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs">{u.role === "super_admin" ? "All (implicit)" : (u.groups || []).map((g) => GROUP_META[g]?.label || g).join(", ") || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${u.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{u.is_active ? "Active" : "Deactivated"}</span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{u.last_login ? String(u.last_login).slice(0, 19).replace("T", " ") : "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing({ ...u, password: "" })} className="text-sky-600 hover:text-sky-800 p-1.5"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(u)} className="text-red-500 hover:text-red-700 p-1.5"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-bold text-[#0a2a5e]">{editing.id ? "Edit User" : "Add User"}</h3>
              <button type="button" onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700 p-1"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Full Name *</label>
                <input type="text" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Email *</label>
                <input type="email" required value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1"><KeyRound className="inline h-3 w-3 mr-0.5" /> Password {editing.id ? "(leave blank to keep current)" : "*"}</label>
                <input type="text" value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} placeholder="Min 8 characters" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                <p className="text-[10px] text-slate-400 mt-1">User will be forced to change this on next login.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Role</label>
                <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin (full access)</option>
                </select>
              </div>
              {editing.role !== "super_admin" && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-2">Module Groups</label>
                  <div className="space-y-2">
                    {Object.entries(GROUP_META).map(([g, meta]) => (
                      <label key={g} className="flex items-start gap-2 rounded-lg border border-slate-200 p-2.5 cursor-pointer hover:bg-slate-50">
                        <input type="checkbox" checked={editing.groups.includes(g)} onChange={() => toggleGroup(g)} className="h-4 w-4 mt-0.5 accent-[#0a2a5e]" />
                        <div>
                          <div className="text-sm font-semibold text-slate-800">{meta.label}</div>
                          <div className="text-xs text-slate-500">{meta.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="h-4 w-4 accent-[#0a2a5e]" />
                Account active
              </label>
            </div>
            <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50 sticky bottom-0">
              <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-full">Cancel</Button>
              <Button type="submit" disabled={saving} className="rounded-full bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white" data-testid="save-user-btn">
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/* Force change-password modal shown on first login */
export function ForceChangePasswordModal({ token, onDone }) {
  const [cur, setCur] = useState(""); const [np, setNp] = useState(""); const [cnf, setCnf] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (np.length < 8) return setErr("Password must be at least 8 characters");
    if (np !== cnf) return setErr("Passwords do not match");
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/auth/change-password`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: cur, new_password: np }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Failed");
      onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-testid="force-change-pw">
        <h3 className="text-lg font-bold text-[#0a2a5e] mb-1">Set a new password</h3>
        <p className="text-sm text-slate-500 mb-4">For security, please change the temporary password before continuing.</p>
        <div className="space-y-3">
          <input type="password" placeholder="Current (temporary) password" required value={cur} onChange={(e) => setCur(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <input type="password" placeholder="New password (min 8 chars)" required value={np} onChange={(e) => setNp(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <input type="password" placeholder="Confirm new password" required value={cnf} onChange={(e) => setCnf(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
        </div>
        <Button type="submit" disabled={busy} className="w-full mt-4 rounded-full bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white">
          {busy ? "Updating…" : "Update Password"}
        </Button>
      </form>
    </div>
  );
}
